import * as appSelectors from 'app/state/app/app-selectors';
import * as loginSelectors from 'app/state/login/login-selectors';
import { loginActions } from 'app/state/login/login-slice';
import * as payloadSelectors from 'app/state/payload/payload-selectors';
import { payloadActions } from 'app/state/payload/payload-slice';
import { signIn, confirmSignIn, signOut } from 'aws-amplify/auth';
import { DEFAULT_FORM_VALUE, FLOWS, loginPhases } from 'common/constants';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Alert } from 'rsuite';
import { Button, Image, Modal } from 'semantic-ui-react';
import { strategyFactory } from 'strategies/strategy-utils';
import { currentAuthenticatedUser } from 'utils/auth-functions';
import { userInputTrimmer } from 'utils/utility-functions';

import { appActions } from '../../app/state/app/app-slice';
import { authSender } from '../../utils/auth-sender';
import { CodeEntry } from './modules/code-entry';
import { EmailEntry } from './modules/email-entry';

const authErrors = {
  timeout: 'Invalid session for the user.',
  invalidEntry: 'Incorrect username or password.',
};

const loginError =
  'You have exceeded the maximum number of login attempts. Please try again.';
const verifyError =
  'You have exceeded the maximum number of verification attempts. Please try again.';
const timeoutError = 'Your session has expired. Please try again.';
const resendCodeError = 'Failed to resend code. Please try again';

export function CognitoAuthModal() {
  const dispatch = useDispatch();
  const signInOutputRef = useRef();

  const isOpen = useSelector(loginSelectors.openLoginModal);
  const loginPhase = useSelector(loginSelectors.loginPhase);

  const place = useSelector(appSelectors.place);
  const license = useSelector(payloadSelectors.license);
  const activeFlow = useSelector(appSelectors.activeFlow);

  const lookupModeEmail = useSelector(payloadSelectors.lookupModeEmail);
  const loggedInUsername = useSelector(loginSelectors.loggedInUsername);
  const renderConfig = useSelector(appSelectors.renderConfig);

  const userEmailRedux = useSelector(loginSelectors.userEmail);
  const isMultiPropertyTOTEnabled = useSelector(
    appSelectors.isMultiPropertyTOTEnabled,
  );
  const [emailValue, setEmailValue] = useState(DEFAULT_FORM_VALUE);

  const [isLoading, setIsLoading] = useState(false);

  const [codeValue, setCodeValue] = useState(DEFAULT_FORM_VALUE);

  const [codeInvalid, setCodeInvalid] = useState(false);
  const [emailInvalid, setEmailInvalid] = useState(false);

  const extractUsername = useMemo(
    () => `${place}::${license?.applicationNumber}`,
    [license?.applicationNumber, place],
  );

  const strategies = renderConfig?.login?.strategies;

  const strategyInstance = useMemo(() => {
    if (!activeFlow) return null;
    return strategyFactory(activeFlow);
  }, [activeFlow]);

  const determineLoginPhase = useCallback(() => {
    if (lookupModeEmail) {
      dispatch(loginActions.setLoginPhase(loginPhases.CODE_ENTRY));
    } else {
      dispatch(loginActions.setLoginPhase(loginPhases.EMAIL_ENTRY));
    }
  }, [dispatch, lookupModeEmail]);

  useEffect(() => {
    determineLoginPhase();
  }, [determineLoginPhase]);

  const resetModal = useCallback(() => {
    dispatch(loginActions.setOpenLoginModal(false));

    signInOutputRef.current = null;

    setEmailValue(DEFAULT_FORM_VALUE);
    setCodeValue(DEFAULT_FORM_VALUE);

    setEmailInvalid(false);
    setCodeInvalid(false);

    determineLoginPhase();
  }, [determineLoginPhase, dispatch]);

  const continueDisabled = useMemo(() => {
    const watchedInput =
      loginPhase === loginPhases.EMAIL_ENTRY ? emailValue : codeValue;
    return !watchedInput;
  }, [emailValue, loginPhase, codeValue]);

  const handleModalClose = useCallback(() => {
    if (isLoading) return;
    dispatch(loginActions.setOpenLoginModal(false));
    resetModal();
  }, [dispatch, isLoading, resetModal]);

  const handleError = useCallback(
    (errorMsg) => {
      Alert.error(errorMsg);
      handleModalClose();
    },
    [handleModalClose],
  );

  const determineEmail = useMemo(() => {
    const email = lookupModeEmail || emailValue || userEmailRedux;
    return email?.trim();
  }, [emailValue, lookupModeEmail, userEmailRedux]);

  const userSignIn = useCallback(async () => {
    const username = extractUsername;
    const signInOutput = await signIn({
      username,
      options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
    });

    if (signInOutput.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
      signInOutputRef.current = await confirmSignIn({
        challengeResponse: determineEmail.toLowerCase(),
      });
    } else {
      signInOutputRef.current = signInOutput;
    }
  }, [extractUsername, determineEmail]);

  const onEmailSubmittedCB = useCallback(async () => {
    setIsLoading(true);
    try {
      setEmailValue((emailVal) => emailVal.trim());
      await userSignIn();

      setIsLoading(false);
      if (signInOutputRef.current?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        batch(() => {
          dispatch(loginActions.setLoginPhase(loginPhases.CODE_ENTRY));
          if (emailValue)
            dispatch(loginActions.setUserEmail(emailValue?.trim()));
        });
      } else {
        throw new Error('Invalid code');
      }
    } catch (err) {
      setIsLoading(false);
      if (err.message === authErrors.invalidEntry) {
        handleError(loginError);
      } else {
        setEmailInvalid(true);
      }
    }
  }, [userSignIn, dispatch, emailValue, handleError]);

  const onCodeSubmittedCB = useCallback(async () => {
    setIsLoading(true);

    try {
      const confirmOutput = await confirmSignIn({
        challengeResponse: codeValue,
      });

      if (confirmOutput.isSignedIn) {
        batch(() => {
          dispatch(payloadActions.setLookupModeEmail(lookupModeEmail));
          dispatch(
            loginActions.setLoggedInUsername({
              username: extractUsername,
            }),
          );
          dispatch(loginActions.setOpenLoginModal(false));
        });

        setIsLoading(false);
        if (activeFlow === FLOWS.PAY_TOT) {
          if (isMultiPropertyTOTEnabled) {
            const allLicensesResponse = await authSender.get('/licenses');
            const allLicenses = allLicensesResponse?.data?.licenses;
            dispatch(payloadActions.setLicenses(allLicenses || []));
            dispatch(
              appActions.setDynamicQuarterSelectedPeriodType(
                allLicenses[0]?.totPaymentPeriod,
              ),
            );
          } else {
            dispatch(
              appActions.setDynamicQuarterSelectedPeriodType(
                license?.totPaymentPeriod,
              ),
            );
          }
        }
        strategyInstance.executeStrategy(strategies, 'onContinueClick');
        resetModal();
      } else {
        throw new Error('Bad login code');
      }
    } catch (err) {
      setIsLoading(false);
      if (err.message === authErrors.invalidEntry) {
        handleError(verifyError);
      } else if (err.message === authErrors.timeout) {
        handleError(timeoutError);
      } else {
        setCodeInvalid(true);
      }
    }
  }, [
    activeFlow,
    codeValue,
    dispatch,
    extractUsername,
    handleError,
    isMultiPropertyTOTEnabled,
    lookupModeEmail,
    resetModal,
    strategies,
    strategyInstance,
    license,
  ]);

  const continueButtonOnClickCB = useCallback(async () => {
    if (loginPhase === loginPhases.EMAIL_ENTRY) await onEmailSubmittedCB();
    else await onCodeSubmittedCB();
  }, [loginPhase, onEmailSubmittedCB, onCodeSubmittedCB]);

  const resendConfirmationCode = async () => {
    setIsLoading(true);
    try {
      await signOut();
    } catch (error) {
      // noop
    }
    try {
      await userSignIn();
      setIsLoading(false);
    } catch (err) {
      handleError(resendCodeError);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    async function tryLogin() {
      const username = extractUsername;
      const usernameFromCognito = await currentAuthenticatedUser();
      if (
        lookupModeEmail &&
        (username !== usernameFromCognito || !loggedInUsername)
      )
        await onEmailSubmittedCB();
    }

    isOpen && tryLogin();
  }, [isOpen, lookupModeEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    async function trySkipLogin() {
      const username = extractUsername;
      const usernameFromCognito = await currentAuthenticatedUser();

      if (username === usernameFromCognito && loggedInUsername) {
        strategyInstance.executeStrategy(strategies, 'onContinueClick');
        resetModal();
      }
    }

    isOpen && trySkipLogin();
  }, [
    dispatch,
    extractUsername,
    strategies,
    isOpen,
    loggedInUsername,
    resetModal,
    strategyInstance,
  ]);

  return (
    <Modal onClose={handleModalClose} open={isOpen} size="small">
      <Modal.Header>Verification</Modal.Header>
      <Modal.Content image>
        <Image src="/assets/email-verification.svg" wrapped />
        <Modal.Description>
          {loginPhase === loginPhases.EMAIL_ENTRY && (
            <EmailEntry
              licenseHolderEmail={license?.registrantEmail}
              emailInputValue={emailValue}
              emailInputValueOnChange={(val) =>
                setEmailValue(userInputTrimmer(val))
              }
              isEmailError={emailInvalid}
            />
          )}

          {loginPhase === loginPhases.CODE_ENTRY && (
            <CodeEntry
              certificateText={renderConfig?.login?.certificateText}
              codeValue={codeValue}
              setCodeValue={(val) => setCodeValue(userInputTrimmer(val))}
              codeInvalid={codeInvalid}
              setCodeInvalid={setCodeInvalid}
              resendConfirmationCode={resendConfirmationCode}
            />
          )}
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button
          basic
          content="Close"
          disabled={isLoading}
          onClick={handleModalClose}
          data-cy="login-modal-close-button"
        />
        <Button
          color="black"
          content="Continue"
          disabled={continueDisabled}
          onClick={continueButtonOnClickCB}
          loading={isLoading}
          data-cy="login-modal-continue-button"
        />
      </Modal.Actions>
    </Modal>
  );
}
